/**
 * KioskMemberLookup - Member lookup by phone/email
 * Enhanced with tier discount display and loyalty points
 * Uses custom numeric keypad for portrait kiosk mode
 */
import { useState } from 'react';
import { User, ChevronLeft, ChevronRight, Search, Loader2, Crown, Star, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useKiosk, LOYALTY_TIERS } from './KioskContext';
import { NumericKeypad } from './KioskKeypad';

export default function KioskMemberLookup() {
  const {
    setView, isAccessible, buttonSizeClass, iconClass,
    loyaltyLookup, setLoyaltyLookup, lookupResults, lookupLoading,
    handleLoyaltyLookup, handleSelectMember
  } = useKiosk();
  
  const [showKeypad, setShowKeypad] = useState(false);

  // Get tier info for display
  const getTierInfo = (tierName) => {
    const tier = tierName?.toLowerCase() || 'new_member';
    return LOYALTY_TIERS[tier] || LOYALTY_TIERS.new_member;
  };

  // Tier badge colors
  const getTierBadgeClass = (tierName) => {
    const tier = tierName?.toLowerCase();
    switch (tier) {
      case 'platinum': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'gold': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'silver': return 'bg-gray-400/20 text-gray-300 border-gray-400/30';
      case 'bronze': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      default: return 'bg-gray-600/20 text-gray-400 border-gray-600/30';
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white p-8" data-testid="kiosk-member-lookup">
      <Button
        variant="ghost"
        onClick={() => setView('welcome')}
        className={`mb-8 text-white ${buttonSizeClass}`}
      >
        <ChevronLeft className={iconClass} />
        <span className={`ml-2 ${isAccessible ? 'text-xl' : ''}`}>Back</span>
      </Button>

      <div className="max-w-xl mx-auto text-center">
        <User className={`${isAccessible ? 'w-24 h-24' : 'w-20 h-20'} mx-auto text-primary mb-6`} />
        <h1 className={`${isAccessible ? 'text-5xl' : 'text-4xl'} font-bold mb-4`}>Welcome Back!</h1>
        <p className={`${isAccessible ? 'text-2xl' : 'text-xl'} text-gray-400 mb-8`}>
          Enter your phone number to find your rewards account
        </p>

        <div className="space-y-6">
          {/* Phone Number Display - Tap to show keypad */}
          <div
            onClick={() => setShowKeypad(true)}
            className={`w-full ${isAccessible ? 'py-8 text-3xl' : 'py-6 text-2xl'} bg-gray-900 border-2 ${showKeypad ? 'border-primary' : 'border-gray-700'} rounded-2xl text-white text-center cursor-pointer transition-colors`}
            data-testid="kiosk-lookup-input"
          >
            {loyaltyLookup || <span className="text-gray-500">Tap to enter phone number</span>}
          </div>
          
          {/* Custom Numeric Keypad */}
          {showKeypad && (
            <NumericKeypad
              onKeyPress={(key) => {
                if (key === 'backspace') {
                  setLoyaltyLookup(prev => prev.slice(0, -1));
                } else if (key === 'clear') {
                  setLoyaltyLookup('');
                } else if (loyaltyLookup.length < 15) {
                  setLoyaltyLookup(prev => prev + key);
                }
              }}
              onDone={() => {
                setShowKeypad(false);
                if (loyaltyLookup.trim()) {
                  handleLoyaltyLookup();
                }
              }}
              doneText="Find My Account"
              isAccessible={isAccessible}
              disabled={!loyaltyLookup.trim()}
            />
          )}
          
          {/* Search Button (only when keypad is hidden) */}
          {!showKeypad && (
            <Button
              onClick={handleLoyaltyLookup}
              disabled={lookupLoading || !loyaltyLookup.trim()}
              className={`w-full ${isAccessible ? 'py-8 text-3xl' : 'py-6 text-2xl'} bg-primary hover:bg-primary/90 rounded-2xl`}
              data-testid="kiosk-lookup-btn"
            >
              {lookupLoading ? (
                <Loader2 className={`${isAccessible ? 'w-10 h-10' : 'w-8 h-8'} animate-spin`} />
              ) : (
                <>
                  <Search className={`${isAccessible ? 'w-10 h-10' : 'w-8 h-8'} mr-3`} />
                  Find My Account
                </>
              )}
            </Button>
          )}
        </div>

        {/* Lookup Results */}
        {lookupResults.length > 0 && (
          <div className="mt-8 space-y-4">
            <p className={`${isAccessible ? 'text-xl' : 'text-lg'} text-gray-400 mb-4`}>
              Select your account:
            </p>
            {lookupResults.map((member) => {
              const tierInfo = getTierInfo(member.loyalty_tier);
              const badgeClass = getTierBadgeClass(member.loyalty_tier);
              
              return (
                <button
                  key={member.contact_id || member.id}
                  onClick={() => handleSelectMember(member)}
                  className={`w-full ${isAccessible ? 'p-6' : 'p-4'} bg-gray-900 hover:bg-gray-800 rounded-2xl flex items-center gap-4 transition-colors border border-gray-800 hover:border-gray-700`}
                  data-testid={`kiosk-member-result-${member.contact_id || member.id}`}
                >
                  <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center">
                    <User className="w-8 h-8 text-primary" />
                  </div>
                  <div className="text-left flex-1">
                    <p className={`${isAccessible ? 'text-2xl' : 'text-xl'} font-medium`}>
                      {member.first_name} {member.last_name}
                    </p>
                    <p className={`${isAccessible ? 'text-lg' : 'text-base'} text-gray-400`}>
                      {member.phone || member.email}
                    </p>
                    {tierInfo.discount > 0 && (
                      <p className="text-green-400 text-sm mt-1 flex items-center gap-1">
                        <Gift className="w-4 h-4" />
                        {tierInfo.discount}% discount on all orders!
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium border ${badgeClass}`}>
                      <Star className="w-3 h-3 inline mr-1" />
                      {tierInfo.name}
                    </span>
                    {member.total_points > 0 && (
                      <p className="text-gray-400 text-sm mt-2">
                        {member.total_points} pts
                      </p>
                    )}
                  </div>
                  <ChevronRight className="w-6 h-6 text-gray-500" />
                </button>
              );
            })}
          </div>
        )}

        {lookupResults.length === 0 && loyaltyLookup && !lookupLoading && (
          <div className="mt-8 text-center">
            <p className={`${isAccessible ? 'text-xl' : 'text-lg'} text-gray-400 mb-4`}>
              Account not found
            </p>
            <Button
              onClick={() => setView('member-signup')}
              variant="outline"
              className={`${isAccessible ? 'py-6 text-2xl' : 'py-4 text-xl'} border-amber-500 text-amber-400 hover:bg-amber-500/20 rounded-xl`}
            >
              <Crown className={`${isAccessible ? 'w-8 h-8' : 'w-6 h-6'} mr-2`} />
              Join Rewards Program
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
